<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { changePassword } from '../lib/auth';
  import { session } from '../lib/session.svelte';
  import { downloadExport } from '../lib/export';
  import FriendsPanel from './FriendsPanel.svelte';
  import type { InviteEntry, UserLink } from '../../../shared/types';
  import { USER_LINK_LIMITS } from '../../../shared/types';

  interface Props {
    onDone: () => void;
  }
  let { onDone }: Props = $props();

  // --- Display name + username + opt-in toggle ------------------------------
  // svelte-ignore state_referenced_locally
  let displayName = $state(session.user?.display_name ?? '');
  // svelte-ignore state_referenced_locally
  let username = $state(session.user?.username ?? '');
  // svelte-ignore state_referenced_locally
  let publicListEnabled = $state(session.user?.public_list_enabled ?? false);
  let displaySaving = $state(false);
  let displayError: string | null = $state(null);
  let displaySaved = $state(false);

  async function saveDisplay(e: SubmitEvent) {
    e.preventDefault();
    displayError = null;
    displaySaved = false;
    displaySaving = true;
    try {
      const next = await api.updateProfile({
        display_name: displayName.trim() ? displayName.trim() : null,
        username: username.trim() ? username.trim().toLowerCase() : null,
        public_list_enabled: publicListEnabled,
      });
      if (session.user) {
        session.user.display_name = next.display_name;
        session.user.username = next.username;
        session.user.public_list_enabled = next.public_list_enabled;
      }
      displaySaved = true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        displayError = 'Brukernavnet er allerede tatt.';
      } else if (err instanceof ApiError && err.status === 400) {
        displayError = 'Brukernavn må være 2–31 tegn: a–z, 0–9, _ eller -.';
      } else {
        displayError = 'Lagring feilet.';
      }
    } finally {
      displaySaving = false;
    }
  }

  // --- Password change ------------------------------------------------------
  let oldPw = $state('');
  let newPw = $state('');
  let confirmPw = $state('');
  let pwError: string | null = $state(null);
  let pwSaving = $state(false);
  let pwChanged = $state(false);

  // --- External profile links ----------------------------------------------
  // svelte-ignore state_referenced_locally
  let linkRows: { label: string; url: string }[] = $state(
    session.user?.links?.map((l) => ({ label: l.label, url: l.url })) ?? [],
  );
  let linksSaving = $state(false);
  let linksError: string | null = $state(null);
  let linksSaved = $state(false);

  function addLinkRow() {
    if (linkRows.length >= USER_LINK_LIMITS.maxPerUser) return;
    linkRows = [...linkRows, { label: '', url: '' }];
  }
  function removeLinkRow(i: number) {
    linkRows = linkRows.filter((_, idx) => idx !== i);
  }

  /** Map a server error code from the links endpoint to a friendly message. */
  function linkErrorMessage(code: string): string {
    switch (code) {
      case 'too_many_links':
        return `Du kan ha maks ${USER_LINK_LIMITS.maxPerUser} lenker.`;
      case 'link_label_required':
        return 'Hver lenke trenger en kort etikett.';
      case 'link_label_too_long':
        return `Etikett kan være maks ${USER_LINK_LIMITS.labelMax} tegn.`;
      case 'link_url_required':
        return 'Hver lenke trenger en adresse.';
      case 'link_url_too_long':
        return 'Adressen er for lang.';
      case 'link_url_malformed':
        return 'En av adressene er ikke en gyldig URL.';
      case 'link_url_bad_protocol':
        return 'Adresser må starte med http:// eller https://.';
      default:
        return 'Lagring feilet.';
    }
  }

  async function saveLinks() {
    linksError = null;
    linksSaved = false;
    linksSaving = true;
    try {
      // Drop empty rows so users can leave half-typed entries without a
      // server rejection — they were probably about to delete that row anyway.
      const cleaned = linkRows
        .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
        .filter((l) => l.label || l.url);
      const next = await api.updateProfile({ links: cleaned });
      if (session.user) session.user.links = next.links;
      linkRows = next.links.map((l: UserLink) => ({ label: l.label, url: l.url }));
      linksSaved = true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const detail = err.detail as { error?: string } | null;
        linksError = linkErrorMessage(detail?.error ?? 'generic');
      } else {
        linksError = 'Lagring feilet.';
      }
    } finally {
      linksSaving = false;
    }
  }

  // --- Invites -------------------------------------------------------------
  let invites: InviteEntry[] = $state([]);
  let invitesError: string | null = $state(null);
  let creating = $state(false);
  let copiedToken: string | null = $state(null);

  async function loadInvites() {
    try {
      invites = await api.listInvites();
    } catch {
      invitesError = 'Kunne ikke laste invitasjoner.';
    }
  }

  onMount(loadInvites);

  async function createInvite() {
    creating = true;
    invitesError = null;
    try {
      const inv = await api.createInvite();
      invites = [inv, ...invites];
    } catch {
      invitesError = 'Kunne ikke opprette invitasjon.';
    } finally {
      creating = false;
    }
  }

  async function cancelInvite(inv: InviteEntry) {
    if (inv.claimed_at) return;
    if (!confirm('Avbryt denne invitasjonen?')) return;
    try {
      await api.cancelInvite(inv.token);
      invites = invites.filter((i) => i.token !== inv.token);
    } catch {
      invitesError = 'Kunne ikke avbryte invitasjonen.';
    }
  }

  /** Build the shareable invite URL using the SPA's own origin. The server
   *  used to compute this but in dev that yielded the API host (port 3000),
   *  not the SPA host (port 5173). The token is the canonical artefact;
   *  the URL is just a presentation concern the SPA owns. Returns null
   *  when the invite has been claimed — the server stops returning the
   *  token at that point and there's nothing left to share. */
  function inviteUrl(inv: InviteEntry): string | null {
    if (!inv.token) return null;
    return `${window.location.origin}/invitasjon/${inv.token}`;
  }

  async function copyInviteUrl(inv: InviteEntry) {
    const url = inviteUrl(inv);
    try {
      await navigator.clipboard.writeText(url);
      copiedToken = inv.token;
      setTimeout(() => { copiedToken = null; }, 1500);
    } catch {
      window.prompt('Kopier denne lenken:', url);
    }
  }

  function formatDate(epochMs: number): string {
    return new Date(epochMs).toLocaleDateString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }

  // --- Export -------------------------------------------------------------
  let exporting = $state(false);
  let exportSize: number | null = $state(null);
  let exportError: string | null = $state(null);

  async function exportNow() {
    exporting = true;
    exportError = null;
    exportSize = null;
    // Without the DEK we'd silently drop every private row from the
    // export. Surface that instead of producing a confusingly small file.
    if (!session.dek) {
      exportError = 'Privat innhold er låst etter sideoppdatering. Lås opp øverst på siden, så får du med alt.';
      exporting = false;
      return;
    }
    try {
      exportSize = await downloadExport();
    } catch {
      exportError = 'Eksport feilet.';
    } finally {
      exporting = false;
    }
  }

  async function savePassword(e: SubmitEvent) {
    e.preventDefault();
    pwError = null;
    pwChanged = false;
    if (newPw.length < 12) {
      pwError = 'Det nye passordet må være minst 12 tegn.';
      return;
    }
    if (newPw !== confirmPw) {
      pwError = 'Passordene må være like.';
      return;
    }
    if (!session.user) {
      pwError = 'Du er ikke logget inn.';
      return;
    }
    pwSaving = true;
    try {
      await changePassword(oldPw, newPw, session.user.email);
      oldPw = '';
      newPw = '';
      confirmPw = '';
      pwChanged = true;
    } catch (err) {
      // Wrong old password → libsodium decrypt failure (server has no way to
      // surface this; the local unwrap is the gate).
      if (err instanceof Error && err.message.toLowerCase().includes('decrypt')) {
        pwError = 'Feil nåværende passord.';
      } else {
        pwError = 'Passordbytte feilet.';
      }
    } finally {
      pwSaving = false;
    }
  }
</script>

<section aria-label="Profil">
  <div class="row" style="justify-content: space-between; margin-bottom: 1rem;">
    <h2 style="margin: 0;">Profil</h2>
    <button type="button" onclick={onDone}>Tilbake</button>
  </div>

  <form onsubmit={saveDisplay} class="card" aria-labelledby="dn-h">
    <h3 id="dn-h">Profil</h3>

    <label for="dn">Visningsnavn</label>
    <input id="dn" type="text" maxlength="50" bind:value={displayName}
           placeholder="f.eks. Ole" />
    <p class="muted" style="margin: 0.25rem 0 0.5rem;">
      Vises på offentlige aktiviteter du legger til. Er feltet tomt og du har
      satt et brukernavn, brukes det i stedet. Ellers vises ingen attribusjon.
    </p>

    <label for="un">Brukernavn for URL</label>
    <input id="un" type="text" maxlength="31" bind:value={username}
           placeholder="f.eks. ole" pattern="[a-z0-9_-]*" />
    <p class="muted" style="margin: 0.25rem 0 0.5rem;">
      Brukes i adressen <code>/{username.trim() || 'brukernavn'}/liste</code>
      hvis du skrur på den offentlige listen nedenfor. Små bokstaver, tall,
      <code>_</code> og <code>-</code>.
    </p>

    <label class="row" style="gap: 0.5rem; align-items: center; margin-top: 0.75rem;">
      <input type="checkbox" bind:checked={publicListEnabled}
             disabled={!username.trim()} />
      <span>Vis offentlig liste på <code>/{username.trim() || 'brukernavn'}/liste</code></span>
    </label>
    {#if publicListEnabled && !username.trim()}
      <p class="muted">Du må sette et brukernavn først.</p>
    {/if}

    {#if displayError}<p class="error" role="alert">{displayError}</p>{/if}
    {#if displaySaved}<p class="muted" role="status">Lagret.</p>{/if}

    <div class="row" style="margin-top: 0.75rem;">
      <button class="primary" type="submit" disabled={displaySaving}>
        {displaySaving ? 'Lagrer …' : 'Lagre'}
      </button>
    </div>
  </form>

  <FriendsPanel />

  <section class="card" aria-labelledby="links-h">
    <h3 id="links-h">Eksterne lenker</h3>
    <p class="muted">
      Legg til opptil {USER_LINK_LIMITS.maxPerUser} lenker — sosiale profiler,
      blogg, hva som helst. De vises på den offentlige listen din (når du har
      skrudd den på).
    </p>

    {#each linkRows as link, i (i)}
      <div class="row" style="margin-bottom: 0.5rem; align-items: stretch;">
        <input
          type="text"
          placeholder="Etikett (f.eks. «Mastodon»)"
          maxlength={USER_LINK_LIMITS.labelMax}
          bind:value={linkRows[i].label}
          style="flex: 1 1 35%; min-width: 9rem;"
          aria-label={`Etikett for lenke ${i + 1}`}
        />
        <input
          type="url"
          placeholder="https://…"
          maxlength={USER_LINK_LIMITS.urlMax}
          bind:value={linkRows[i].url}
          style="flex: 2 1 50%; min-width: 12rem;"
          aria-label={`URL for lenke ${i + 1}`}
        />
        <button class="danger" type="button"
                onclick={() => removeLinkRow(i)}
                aria-label={`Fjern lenke ${i + 1}`}>
          Fjern
        </button>
      </div>
    {/each}

    {#if linkRows.length < USER_LINK_LIMITS.maxPerUser}
      <button type="button" onclick={addLinkRow}>+ Legg til lenke</button>
    {/if}

    {#if linksError}<p class="error" role="alert">{linksError}</p>{/if}
    {#if linksSaved}<p class="muted" role="status">Lagret.</p>{/if}

    <div class="row" style="margin-top: 0.75rem;">
      <button class="primary" type="button" onclick={saveLinks} disabled={linksSaving}>
        {linksSaving ? 'Lagrer …' : 'Lagre lenker'}
      </button>
    </div>
  </section>

  <section class="card" aria-labelledby="exp-h">
    <h3 id="exp-h">Eksporter</h3>
    <p class="muted">
      Last ned alle aktivitetene du kan se — inkludert dine private — som
      en Markdown-fil. Filen lages i nettleseren din; serveren får aldri
      klartekst.
    </p>
    {#if exportError}<p class="error" role="alert">{exportError}</p>{/if}
    {#if exportSize !== null}
      <p class="muted" role="status">Eksportert ({(exportSize / 1024).toFixed(1)} kB).</p>
    {/if}
    <div class="row">
      <button type="button" onclick={exportNow} disabled={exporting}>
        {exporting ? 'Eksporterer …' : 'Last ned eksport'}
      </button>
    </div>
  </section>

  <section class="card" aria-labelledby="inv-h">
    <h3 id="inv-h">Invitasjoner</h3>
    <p class="muted">
      Generer en lenke du kan dele. Den som registrerer seg via lenken blir
      knyttet til deg som invitør. Hver lenke kan bare brukes én gang.
    </p>

    {#if invitesError}<p class="error" role="alert">{invitesError}</p>{/if}

    <div class="row" style="margin-bottom: 0.5rem;">
      <button class="primary" type="button" onclick={createInvite} disabled={creating}>
        {creating ? 'Lager …' : 'Ny invitasjon'}
      </button>
    </div>

    {#if invites.length === 0}
      <p class="muted">Ingen invitasjoner ennå.</p>
    {/if}
    {#each invites as inv (inv.token ?? `c-${inv.created_at}-${inv.claimed_at}`)}
      {#if inv.claimed_at}
        <!-- Claimed: one-line summary. The token isn't returned anymore,
             so there's nothing to copy or cancel — just the audit trail. -->
        <p class="muted" style="margin: 0.4rem 0; font-size: 0.9rem;">
          ✓ Laget {formatDate(inv.created_at)} ·
          {#if inv.claimed_by_display}
            godtatt av {inv.claimed_by_display} {formatDate(inv.claimed_at)}
          {:else}
            godtatt {formatDate(inv.claimed_at)}
          {/if}
        </p>
      {:else}
        <article class="card" style="margin-top: 0.5rem;">
          <div class="row" style="justify-content: space-between;">
            <code style="font-size: 0.8rem; word-break: break-all;">{inviteUrl(inv)}</code>
            <span class="muted" style="white-space: nowrap;">{formatDate(inv.created_at)}</span>
          </div>
          <div class="row" style="margin-top: 0.5rem;">
            <button type="button" onclick={() => copyInviteUrl(inv)}>
              {copiedToken === inv.token ? 'Kopiert!' : 'Kopier lenke'}
            </button>
            <button class="danger" type="button" onclick={() => cancelInvite(inv)}>
              Avbryt
            </button>
          </div>
        </article>
      {/if}
    {/each}
  </section>

  <form onsubmit={savePassword} class="card" aria-labelledby="pw-h">
    <h3 id="pw-h">Bytt passord</h3>
    <p class="muted">
      Dataene dine forblir kryptert med samme nøkkel — bare innpakningen byttes.
      Gjenopprettingskoden virker fortsatt.
    </p>

    <label for="pw-old">Nåværende passord</label>
    <input id="pw-old" type="password" autocomplete="current-password"
           bind:value={oldPw} required />

    <label for="pw-new">Nytt passord (minst 12 tegn)</label>
    <input id="pw-new" type="password" autocomplete="new-password" minlength="12"
           bind:value={newPw} required />

    <label for="pw-confirm">Bekreft nytt passord</label>
    <input id="pw-confirm" type="password" autocomplete="new-password" minlength="12"
           bind:value={confirmPw} required />

    {#if pwError}<p class="error" role="alert">{pwError}</p>{/if}
    {#if pwChanged}<p class="muted" role="status">Passordet er endret.</p>{/if}

    <div class="row" style="margin-top: 0.75rem;">
      <button class="primary" type="submit" disabled={pwSaving}>
        {pwSaving ? 'Bytter …' : 'Bytt passord'}
      </button>
    </div>
  </form>
</section>
